import React, { useState } from 'react';
import { generateImage } from '../services/geminiService';
import { SparklesIcon } from '../components/icons';

const ImageGenerationView: React.FC = () => {
    const [prompt, setPrompt] = useState('');
    const [generatedImage, setGeneratedImage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGenerate = async () => {
        if (!prompt.trim()) {
            setError('Please enter a prompt.');
            return;
        }
        setIsLoading(true);
        setError(null);
        setGeneratedImage(null);

        try {
            const imageUrl = await generateImage(prompt);
            setGeneratedImage(imageUrl);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto h-full">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold">Image Generation</h2>
                <p className="text-text-secondary mt-2">Describe the image you want to create for your marketing campaign.</p>
            </div>

            <div className="space-y-4">
                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., A futuristic robot marketer analyzing data on a holographic screen, digital art"
                    className="w-full bg-base-200 rounded-lg p-3 border border-base-300 focus:border-brand-primary focus:outline-none resize-none"
                    rows={3}
                />
                <button
                    onClick={handleGenerate}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-secondary text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300 disabled:bg-base-300"
                >
                    {isLoading ? 'Generating...' : 'Generate Image'}
                    {!isLoading && <SparklesIcon className="w-5 h-5" />}
                </button>
            </div>

            {error && <p className="text-red-400 text-center mt-4">{error}</p>}

            <div className="mt-8 flex justify-center items-center">
                {isLoading && (
                    <div className="w-64 h-64 border-4 border-dashed rounded-lg flex flex-col items-center justify-center text-text-secondary border-base-300">
                         <div className="w-12 h-12 border-4 border-dashed rounded-full animate-spin border-brand-primary mb-4"></div>
                         <p>Creating your image...</p>
                    </div>
                )}
                {generatedImage && (
                    <div className="bg-base-200 p-2 rounded-lg border border-base-300">
                         <img src={generatedImage} alt="Generated by AI" className="rounded-md max-w-full h-auto object-contain" style={{maxWidth: '512px'}}/>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageGenerationView;
